import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner, Box, Divider, StackView } from '@telus-uds/components-web';

import useLinkVerification from '../../hooks/useLinkVerification';
import useLinkSubmit from '../../hooks/useLinkSubmit';
import useLinkPreload from '../../hooks/useLinkPreload';
import PaymentSummary from '../../components/payment-summary/PaymentSummary';
import MainLayout from '../../components/main-layout/MainLayout';
import { ReasonsToTrust } from '../../components/reasons-to-trust';
import { MethodTypeSelection } from '../../components/method-type-selection/MethodTypeSelection';
import ProcessingOverlay from '../../components/processing-overlay/ProcessingOverlay';
import { useTheme } from '../../BrandThemeProvider';
import { linkFormContext } from '../../contexts/linkFormContext';

function LinkFormPage() {
  const navigate = useNavigate();
  const { switchThemeAndLanguage, switchTestMode } = useTheme();
  const { t, i18n } = useTranslation();

  const { linkToken } = useParams();
  const { preloadLink, processing: processingPreload } = useLinkPreload(linkToken);
  const { verifyLink, processing: processingVerification } = useLinkVerification(linkToken);
  const { submitLink, processing: processingSubmit } = useLinkSubmit();

  const [linkType, setLinkType] = useState('Payment');
  const [allowedPaymentMethods, setAllowedPaymentMethods] = useState([]);
  const [sessionToken, setSessionToken] = useState(null);
  const [plaidLinkToken, setPlaidLinkToken] = useState(null);

  const [clientId, setClientId] = useState(null);
  const [avsPostalChk, setAvsPostalChk] = useState(false);
  const [paymentItems, setPaymentItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState({});
  const [outcome, setOutcome] = useState(null);
  const [disclaimers, setDisclaimers] = useState([]);
  const [title, setTitle] = useState({});
  const [disclaimerConsents, setDisclaimerConsents] = useState({});
  const [epsEnv, setEpsEnv] = useState(null);
  const [signatureRequired, setSignatureRequired] = useState(false);
  const [signature, setSignature] = useState(undefined);

  const [cardToken, setCardToken] = useState(null);
  const [plaidPublicToken, setPlaidPublicToken] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [methodCaptureError, setMethodCaptureError] = useState(null);

  // preload and verification handlers
  useEffect(() => {
    const preload = async () => {
      try {
        const preloadResponse = await preloadLink();
        const browserLang = navigator.language?.split('-')[0];
        const supportedLangs = i18n.languages;
        const resolvedLang = supportedLangs.includes(browserLang) ? browserLang : preloadResponse.language;
        switchThemeAndLanguage(preloadResponse.brand, resolvedLang);
        switchTestMode(preloadResponse.xTest);
        setOutcome(preloadResponse.outcome);
      } catch (err) {
        console.error('Link preload error');
        navigate('/status', {
          state: {
            status: 'failure',
            outcome: outcome?.content,
            message: err.message || t('InvalidLink'),
          },
        });
      }
    };
    preload();
  }, []);

  useEffect(() => {
    const verify = async () => {
      try {
        const response = await verifyLink();
        const { link, session, env } = response;
        const { title, disclaimer, paymentItem, totalAmount, allowedPaymentMethods, signature, paymentClientID } = link;
        const type = link['@type'];
        const { token, avsPostalChk, plaidLinkToken } = session;

        setLinkType(type);
        setEpsEnv(env);
        setClientId(paymentClientID);
        setAllowedPaymentMethods(allowedPaymentMethods);
        setSessionToken(token);
        setPlaidLinkToken(plaidLinkToken);
        setAvsPostalChk(avsPostalChk);
        setTitle(title);
        setDisclaimers(disclaimer || []);
        setSignatureRequired(signature?.required || false);
        if (type === 'Payment') {
          setPaymentItems(paymentItem);
          setTotalAmount(totalAmount);
        }
      } catch (err) {
        console.error('Link verification error');
        navigate('/status', {
          state: {
            status: 'failure',
            outcome: outcome?.content,
            message: err.message || t('InvalidLink'),
          },
        });
      }
    };
    if (outcome) verify();
  }, [outcome]);

  useEffect(() => {
    if (methodCaptureError) {
      navigate('/status', {
        state: {
          status: 'failure',
          outcome: outcome?.content,
          message: methodCaptureError,
          shouldRetry: true,
          path: linkToken,
        },
      });
    }
  }, [methodCaptureError]);

  // link submit handler
  useEffect(() => {
    const submit = async () => {
      if (!submitting) return;

      try {
        await submitLink({ linkToken, cardToken, sessionToken, plaidPublicToken, signature });
        navigate('/status', {
          state: {
            status: 'success',
            outcome: outcome?.content,
          },
        });
      } catch (err) {
        console.error('Link processing error');
        navigate('/status', {
          state: {
            status: 'failure',
            outcome: outcome?.content,
            message: err.message || t('InvalidLink'),
            shouldRetry: true,
            path: linkToken,
          },
        });
      } finally {
        setSubmitting(false);
      }
    };
    submit();
  }, [submitting]);

  if (processingPreload || processingVerification) {
    return (
      <StackView
        tokens={{
          alignItems: 'center',
          justifyContent: 'center',
          flexGrow: 1,
        }}
      >
        <Spinner show label={t('verifying')} />
      </StackView>
    );
  }

  return (
    <MainLayout title={title?.content?.[i18n.language]}>
      <ProcessingOverlay isProcessing={submitting} type={linkType} />
      {sessionToken && (
        <Box bottom={{ xs: 2, sm: 3, lg: 5, xl: 6 }} top={1}>
          <Box space={{ bottom: 4 }} alignItems="center"></Box>
          {linkType === 'Payment' && (
            <>
              <Box space={{ bottom: 4 }}>
                <PaymentSummary paymentItems={paymentItems} totalAmount={totalAmount} />
              </Box>
              <Divider space={4} />
            </>
          )}
          <linkFormContext.Provider
            value={{
              linkType,
              sessionToken,
              avsPostalChk,
              epsEnv,
              clientId,
              disclaimers,
              disclaimerConsents,
              setDisclaimerConsents,
              setCardToken,
              plaidLinkToken,
              setPlaidPublicToken,
              allowedPaymentMethods,
              selectedMethod,
              setSelectedMethod,
              setSubmitting,
              setMethodCaptureError,
              signatureRequired,
              setSignature,
            }}
          >
            <Box space={{ bottom: 4 }}>
              <MethodTypeSelection />
            </Box>
            <ReasonsToTrust />
          </linkFormContext.Provider>
        </Box>
      )}
    </MainLayout>
  );
}

export default LinkFormPage;
